import { useContext, createContext } from 'react';
import { useConfig } from '../Config/ConfigProvider';
import {
  NavigateOptions,
  NextgenRouterContextInterface,
  PrefetchOptions,
  RouterProviderInterface,
  ReadonlyURLSearchParams,
  Params,
} from './types/propsType';
import { genericRouter, genericPathParams, genericPathname, genericSearchParams } from './fallbackRouterObject';
import useRouterWithEvents from './routerWithEvents';

// Template
const genericNextgenRouterTemplate: NextgenRouterContextInterface = {
  ...genericRouter,
  params: genericPathParams,
  pathname: genericPathname,
  query: genericSearchParams,
};

// Next Router Context
const NextgenRouterContext = createContext<NextgenRouterContextInterface>(genericNextgenRouterTemplate);

/**
 * Next Router Provider
 *
 * @argument method takes object containing return value of useROuter(), usePathname() and useParams().
 * @example method = {
 *   ...router : useRouter(), // spread router
 *   pathname  : usePathname(),
 *   query     : useSearchParams()
 *   params    : useParams()
 * }
 *
 */
function NextgenRouterProvider({ method, children, isNext14 = false }: RouterProviderInterface): JSX.Element {
  const configs = useConfig();
  const updatedRouterWithEvents = useRouterWithEvents(method, configs.NEXT_PUBLIC_LANGUAGE as string, isNext14);
  return (
    <NextgenRouterContext.Provider value={updatedRouterWithEvents ?? genericNextgenRouterTemplate}>
      {children}
    </NextgenRouterContext.Provider>
  );
}
/**
 * @description useNextgenRouter is updated with Nextjs 13 upgrades
 * @tutorial https://nextjs.org/docs/app/building-your-application/upgrading/app-router-migration#step-5-migrating-routing-hooks for more details.
 *
 */
const useNextgenRouter = () => {
  const nextgenRouterObject = useContext(NextgenRouterContext);
  return { ...nextgenRouterObject };
};

export { useNextgenRouter, NextgenRouterProvider, genericNextgenRouterTemplate };
export type { NavigateOptions, PrefetchOptions, ReadonlyURLSearchParams, Params };
export default NextgenRouterProvider;
